import { useState, type KeyboardEvent, type MouseEvent } from 'react'
import {
  ClipboardCopyIcon,
  ClipboardPasteIcon,
  EllipsisVerticalIcon,
  PlusIcon,
  Trash2Icon
} from 'lucide-react'
import {
  addDays,
  dateFromString,
  findOverlappingEntries,
  formatDecimalHours,
  localDate,
  type Customer,
  type Person,
  type Project,
  type Task,
  type TimeEntry
} from '@coneno/c-sheet-shared'
import { Button } from '@/components/ui/button'
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuGroup,
  ContextMenuItem
} from '@/components/ui/context-menu'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import type { DayClipboardStatus } from '@/hooks/use-timesheets'
import { cn } from '@/lib/utils'
import type { EntryRow } from '../hooks/use-entry-draft'
import { hoursCell } from '../lib/labels'
import { EntryChip } from './entry-chip'

/** Full-width add button in a grid cell, shown while its row is hovered or the button is focused. */
const addEntryButtonClass =
  'w-full border border-dashed border-transparent text-muted-foreground opacity-0 group-hover/row:border-border group-hover/row:opacity-100 hover:border-primary/60 hover:text-primary focus-visible:opacity-100'

type Day = { date: string; isWeekend: boolean; entries: TimeEntry[]; total: number }

type WeekGridProps = {
  weekStart: string
  /** Entries of the shown week. */
  entries: TimeEntry[]
  people: Person[] | undefined
  projects: Project[] | undefined
  customers: Customer[] | undefined
  tasks: Task[] | undefined
  showEveryone: boolean
  onShowEveryone(): void
  isLoading: boolean
  error: { message: string } | null
  hasActiveProjects: boolean
  dayClipboard: DayClipboardStatus
  copyingDay: boolean
  pastingDay: boolean
  deleting: boolean
  onCopyDay(date: string): void
  onPasteDay(date: string): void
  onClearDay(entries: TimeEntry[], date: string): void
  onAddForPerson(anchor: HTMLElement, date: string, personId: string): void
  onAddFromRow(anchor: HTMLElement, date: string, row: EntryRow): void
  onEdit(anchor: HTMLElement, entry: TimeEntry): void
  onClone(entry: TimeEntry): void
  onDelete(entry: TimeEntry): void
}

// Arrow keys move between the entries and add buttons of the grid. Left and right go from cell to
// cell and wrap to the neighbouring row; up and down go through a cell's items, then the next row.
function handleGridKeyDown(event: KeyboardEvent<HTMLButtonElement>): void {
  if (
    !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key) ||
    event.defaultPrevented ||
    event.nativeEvent.isComposing ||
    event.altKey ||
    event.ctrlKey ||
    event.metaKey ||
    event.shiftKey
  ) {
    return
  }

  const cell = event.currentTarget.closest('td')
  const row = cell?.parentElement
  const body = row?.parentElement
  if (!cell || !(row instanceof HTMLTableRowElement) || !body) return

  event.preventDefault()
  const targets = (element: Element): HTMLButtonElement[] =>
    Array.from(element.querySelectorAll<HTMLButtonElement>('[data-grid-nav]:not(:disabled)'))
  const rows = Array.from(body.children).filter(
    (item): item is HTMLTableRowElement => item instanceof HTMLTableRowElement
  )

  if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
    const step = event.key === 'ArrowUp' ? -1 : 1
    const items = targets(cell)
    const sibling = items[items.indexOf(event.currentTarget) + step]
    if (sibling) {
      sibling.focus()
      return
    }
    for (let index = rows.indexOf(row) + step; index >= 0 && index < rows.length; index += step) {
      const nextCell = rows[index]?.cells[cell.cellIndex]
      const nextItems = nextCell ? targets(nextCell) : []
      if (nextItems.length) {
        nextItems[step > 0 ? 0 : nextItems.length - 1]?.focus()
        return
      }
    }
    return
  }

  const cells = rows
    .flatMap((item) => Array.from(item.cells))
    .filter((item) => targets(item).length)
  const nextCell = cells[cells.indexOf(cell) + (event.key === 'ArrowLeft' ? -1 : 1)]
  if (nextCell) targets(nextCell)[0]?.focus()
}

/** The week as a table: one row per person and project, a column per day, totals at the edges. */
export function WeekGrid({
  weekStart,
  entries,
  people,
  projects,
  customers,
  tasks,
  showEveryone,
  onShowEveryone,
  isLoading,
  error,
  hasActiveProjects,
  dayClipboard,
  copyingDay,
  pastingDay,
  deleting,
  onCopyDay,
  onPasteDay,
  onClearDay,
  onAddForPerson,
  onAddFromRow,
  onEdit,
  onClone,
  onDelete
}: WeekGridProps): React.JSX.Element {
  const [hoveredDay, setHoveredDay] = useState<string | null>(null)

  const days: Day[] = Array.from({ length: 7 }, (_, index) => {
    const date = addDays(weekStart, index)
    const dayEntries = entries.filter((entry) => entry.workDate === date)
    return {
      date,
      isWeekend: index >= 5,
      entries: dayEntries,
      total: dayEntries.reduce((sum, entry) => sum + entry.durationMinutes, 0)
    }
  })
  const weekTotal = days.reduce((sum, day) => sum + day.total, 0)

  const customerNames = new Map(customers?.map((customer) => [customer.id, customer.name]))
  const projectsById = new Map(projects?.map((project) => [project.id, project]))
  const taskNames = new Map(tasks?.map((task) => [task.id, task.name]))

  const rowsByKey = new Map<string, EntryRow & { key: string; entries: TimeEntry[] }>()
  for (const entry of entries) {
    const key = JSON.stringify([entry.personId, entry.projectId, entry.taskId])
    const row = rowsByKey.get(key) ?? {
      key,
      personId: entry.personId,
      projectId: entry.projectId,
      taskId: entry.taskId,
      entries: []
    }
    row.entries.push(entry)
    rowsByKey.set(key, row)
  }
  const rowLabel = (row: EntryRow): string =>
    `${row.personId} ${projectsById.get(row.projectId)?.name ?? ''} ${taskNames.get(row.taskId ?? '') ?? ''}`
  const matrixRows = Array.from(rowsByKey.values()).sort((left, right) =>
    rowLabel(left).localeCompare(rowLabel(right))
  )

  const peopleWithEntries = new Set(entries.map((entry) => entry.personId))
  const listedPeople =
    people?.filter((person) => person.active || peopleWithEntries.has(person.id)) ?? []
  // By default only people with time this week are listed; with nobody listed, show everyone so
  // there is somewhere to add the first entry.
  const visiblePeople =
    showEveryone || !peopleWithEntries.size
      ? listedPeople
      : listedPeople.filter((person) => peopleWithEntries.has(person.id))
  const hiddenPeopleCount = listedPeople.length - visiblePeople.length
  const overlappingEntryIds = findOverlappingEntries(entries)

  function updateHoveredDay(event: MouseEvent<HTMLTableElement>): void {
    const target = event.target
    const dayCell = target instanceof Element ? target.closest<HTMLElement>('[data-day]') : null
    setHoveredDay(dayCell?.dataset.day ?? null)
  }

  function dayMenuItems(day: Day, context = false): React.JSX.Element {
    const Item = context ? ContextMenuItem : DropdownMenuItem
    return (
      <>
        <Item disabled={!day.entries.length || copyingDay} onClick={() => onCopyDay(day.date)}>
          <ClipboardCopyIcon />
          Copy day
        </Item>
        <Item
          disabled={!dayClipboard.canPaste || pastingDay}
          title={dayClipboard.canPaste ? undefined : 'Copy a day first.'}
          onClick={() => onPasteDay(day.date)}
        >
          <ClipboardPasteIcon />
          Paste into day
        </Item>
        <Item
          variant="destructive"
          disabled={!day.entries.length || deleting}
          onClick={() => onClearDay(day.entries, day.date)}
        >
          <Trash2Icon />
          Clear day
        </Item>
      </>
    )
  }

  return (
    <section aria-label="Weekly time entries" className="overflow-hidden rounded-lg border bg-card">
      {isLoading ? (
        <div className="flex flex-col gap-2 p-3" aria-label="Loading entries">
          <Skeleton className="h-9 w-full" />
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-11 w-full opacity-70" />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table
            className="w-full min-w-[900px] table-fixed border-collapse text-sm"
            onMouseOver={updateHoveredDay}
            onMouseLeave={() => setHoveredDay(null)}
          >
            <colgroup>
              <col className="w-52" />
              {days.map((day) => (
                <col key={day.date} className={day.isWeekend ? 'w-24' : undefined} />
              ))}
              <col className="w-20" />
            </colgroup>
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                  Person / project
                </th>
                {days.map((day) => (
                  <ContextMenu key={day.date}>
                    <ContextMenuTrigger
                      render={<th />}
                      data-day={day.date}
                      aria-current={day.date === localDate() ? 'date' : undefined}
                      className={cn(
                        'relative border-l px-7 py-2 text-center',
                        day.isWeekend && 'bg-muted/60',
                        day.date === localDate() && 'bg-primary/10'
                      )}
                    >
                      <div className="flex flex-col items-center gap-0.5">
                        <span
                          className={cn(
                            'text-[11px] font-medium tracking-wide whitespace-nowrap text-muted-foreground uppercase',
                            day.date === localDate() && 'font-bold text-primary'
                          )}
                        >
                          {new Intl.DateTimeFormat(undefined, { weekday: 'short' }).format(
                            dateFromString(day.date)
                          )}
                        </span>
                        <span
                          className={cn(
                            'flex size-8 items-center justify-center rounded-full text-lg font-medium tabular-nums',
                            day.date === localDate() && 'bg-primary text-primary-foreground'
                          )}
                          aria-label={new Intl.DateTimeFormat(undefined, {
                            month: 'long',
                            day: 'numeric'
                          }).format(dateFromString(day.date))}
                        >
                          {dateFromString(day.date).getDate()}
                        </span>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          render={
                            <Button
                              size="icon-xs"
                              variant="muted-ghost"
                              className={cn(
                                'absolute top-1/2 right-1 -translate-y-1/2 opacity-0 focus-visible:opacity-100',
                                hoveredDay === day.date && 'opacity-100'
                              )}
                              aria-label={`Actions for ${day.date}`}
                            />
                          }
                        >
                          <EllipsisVerticalIcon />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="min-w-48" side="bottom" align="end">
                          <DropdownMenuGroup>{dayMenuItems(day)}</DropdownMenuGroup>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </ContextMenuTrigger>
                    <ContextMenuContent className="min-w-48">
                      <ContextMenuGroup>{dayMenuItems(day, true)}</ContextMenuGroup>
                    </ContextMenuContent>
                  </ContextMenu>
                ))}
                <th className="border-l px-3 py-2 text-right align-middle text-xs font-medium whitespace-nowrap text-muted-foreground">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {visiblePeople.length ? (
                visiblePeople.map((person) => {
                  const personRows = matrixRows.filter((row) => row.personId === person.id)
                  const personTotal = personRows
                    .flatMap((row) => row.entries)
                    .reduce((sum, entry) => sum + entry.durationMinutes, 0)
                  return [
                    <tr key={`person:${person.id}`} className="group/row border-b bg-muted/25">
                      <th className="px-3 py-2 text-left font-semibold">
                        {person.name}
                        {!person.active && (
                          <span className="ml-1 text-xs font-normal text-muted-foreground">
                            (inactive)
                          </span>
                        )}
                      </th>
                      {days.map((day) => (
                        <td
                          key={day.date}
                          data-day={day.date}
                          className={cn(
                            'border-l p-1 text-center align-top',
                            day.isWeekend && 'bg-muted/40'
                          )}
                        >
                          <Button
                            size="sm"
                            variant="muted-ghost"
                            className={addEntryButtonClass}
                            disabled={!hasActiveProjects || !person.active}
                            data-add-entry=""
                            data-grid-nav=""
                            onKeyDown={handleGridKeyDown}
                            aria-label={`Add entry for ${person.name} on ${day.date}`}
                            onClick={(event) =>
                              onAddForPerson(event.currentTarget, day.date, person.id)
                            }
                          >
                            <PlusIcon />
                          </Button>
                        </td>
                      ))}
                      <td className="border-l px-3 py-2 text-right font-semibold tabular-nums">
                        {hoursCell(personTotal)}
                      </td>
                    </tr>,
                    ...personRows.map((row) => {
                      const rowTotal = row.entries.reduce(
                        (sum, entry) => sum + entry.durationMinutes,
                        0
                      )
                      const project = projectsById.get(row.projectId)
                      const customerName = project?.customerId
                        ? customerNames.get(project.customerId)
                        : null
                      const projectLabel = `${project?.name ?? 'Unknown project'}${project?.code ? ` (${project.code})` : ''}`
                      const contextLabel = [
                        customerName ?? 'Internal',
                        row.taskId ? (taskNames.get(row.taskId) ?? 'Unknown task') : null
                      ]
                        .filter(Boolean)
                        .join(' · ')
                      return (
                        <tr key={row.key} className="group/row border-b last:border-b-0">
                          <th className="py-2 pr-3 pl-7 text-left align-top font-normal">
                            <p className="truncate font-medium" title={projectLabel}>
                              {projectLabel}
                            </p>
                            <p
                              className="truncate text-xs text-muted-foreground"
                              title={contextLabel}
                            >
                              {contextLabel}
                            </p>
                          </th>
                          {days.map((day) => {
                            const cellEntries = row.entries
                              .filter((entry) => entry.workDate === day.date)
                              .sort((a, b) => a.startTime.localeCompare(b.startTime))
                            // In a row with entries, an empty cell's add button is as tall as an entry.
                            const matchEntryHeight = !cellEntries.length && row.entries.length > 0
                            return (
                              <td
                                key={day.date}
                                data-day={day.date}
                                className={cn(
                                  'border-l p-1 text-center align-top',
                                  day.isWeekend && 'bg-muted/40'
                                )}
                              >
                                <div className="flex flex-col items-stretch gap-1">
                                  {cellEntries.map((entry) => (
                                    <EntryChip
                                      key={entry.id}
                                      startTime={entry.startTime}
                                      endTime={entry.endTime}
                                      durationMinutes={entry.durationMinutes}
                                      description={entry.description}
                                      warning={
                                        overlappingEntryIds.has(entry.id)
                                          ? 'Overlaps another entry of this person.'
                                          : undefined
                                      }
                                      onClone={() => onClone(entry)}
                                      onDelete={() => onDelete(entry)}
                                      deleting={deleting}
                                      onKeyDown={handleGridKeyDown}
                                      ariaLabel={`Edit ${entry.startTime}–${entry.endTime} for ${person.name} on ${day.date}`}
                                      onEdit={(anchor) => onEdit(anchor, entry)}
                                    />
                                  ))}
                                  <Button
                                    size="sm"
                                    variant="muted-ghost"
                                    className={cn(
                                      addEntryButtonClass,
                                      cellEntries.length > 0 && 'h-6',
                                      matchEntryHeight && 'h-auto gap-0 py-1'
                                    )}
                                    data-add-entry=""
                                    data-grid-nav=""
                                    onKeyDown={handleGridKeyDown}
                                    aria-label={`Add entry for ${person.name} on ${day.date}`}
                                    onClick={(event) =>
                                      onAddFromRow(event.currentTarget, day.date, row)
                                    }
                                  >
                                    {matchEntryHeight && (
                                      // Same two lines as an entry card, so the button is as tall.
                                      <span
                                        aria-hidden="true"
                                        className="invisible flex w-0 flex-col"
                                      >
                                        <span className="text-sm">0</span>
                                        <span className="text-[11px]">0</span>
                                      </span>
                                    )}
                                    <PlusIcon />
                                  </Button>
                                </div>
                              </td>
                            )
                          })}
                          <td className="border-l px-3 py-2 text-right align-top font-semibold tabular-nums">
                            {hoursCell(rowTotal)}
                          </td>
                        </tr>
                      )
                    })
                  ]
                })
              ) : (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    Add an active person to start entering time.
                  </td>
                </tr>
              )}
              {hiddenPeopleCount > 0 && (
                <tr className="border-t">
                  <td colSpan={9} className="px-3 py-2 text-xs text-muted-foreground">
                    {hiddenPeopleCount} {hiddenPeopleCount === 1 ? 'person' : 'people'} without
                    entries this week ·{' '}
                    <button
                      type="button"
                      className="font-medium text-primary underline-offset-2 hover:underline"
                      onClick={onShowEveryone}
                    >
                      Show
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="border-t bg-muted/40 font-semibold">
                <th className="px-3 py-2 text-left text-xs">Daily total</th>
                {days.map((day) => (
                  <td
                    key={day.date}
                    data-day={day.date}
                    className={cn(
                      'border-l px-1 py-2 text-center tabular-nums',
                      day.isWeekend && 'bg-muted/60'
                    )}
                  >
                    {hoursCell(day.total)}
                  </td>
                ))}
                <td className="border-l bg-primary/10 px-3 py-1.5 text-right align-middle text-primary tabular-nums">
                  <span className="block text-[10px] font-bold tracking-wide uppercase">Week</span>
                  <span className="text-base">{formatDecimalHours(weekTotal)} h</span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      {error && <p className="border-t p-3 text-sm text-destructive">{error.message}</p>}
    </section>
  )
}
